import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, auth } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Phone } from "lucide-react";

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [mockOtp, setMockOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (mobile.length < 10) return toast.error("Enter a valid 10-digit mobile");
    setLoading(true);
    try {
      const r = await api.post("/auth/send-otp", { mobile, role: "partner" });
      setOtpSent(true);
      setMockOtp(r.data.otp);
      toast.success(`OTP sent (mock): ${r.data.otp}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) return toast.error("Enter OTP");
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { mobile, otp, role: "partner" });
      if (r.data.is_new_user) {
        toast.success("Verified. Complete partner registration.");
        navigate(`/partner/register?mobile=${mobile}`);
      } else {
        auth.setSession(r.data.token, "partner", r.data.user);
        toast.success("Welcome back, partner!");
        navigate("/partner/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar variant="dashboard" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="rounded-3xl overflow-hidden border border-[#E5E3DB] h-96 lg:h-full relative">
            <img
              src="https://images.unsplash.com/photo-1682345262055-8f95f3c513ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHdvcmtpbmclMjBtb2Rlcm58ZW58MHx8fHwxNzgyNzE0MzI3fDA&ixlib=rb-4.1.0&q=85"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8 text-white">
              <div className="font-display text-3xl">Grow with OCTA</div>
              <div className="text-sm opacity-90 mt-2">
                Get qualified leads in your pincode.
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 flex items-center">
          <div className="w-full max-w-md mx-auto fade-in">
            <div className="text-xs uppercase tracking-widest text-[#E06D53] font-semibold">
              Partner Login
            </div>
            <h1 className="font-display text-4xl mt-3">Sign in with OTP</h1>
            <p className="text-[#4A4A4A] mt-3">
              Enter your mobile to receive an OTP.
            </p>

            <form onSubmit={otpSent ? verifyOtp : sendOtp} className="mt-10 space-y-5">
              <div>
                <label className="text-sm font-medium">Mobile number</label>
                <div className="relative mt-2">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    placeholder="10-digit mobile"
                    className="octa-input pl-11"
                    disabled={otpSent}
                    data-testid="partner-mobile-input"
                  />
                </div>
              </div>

              {otpSent && (
                <div>
                  <label className="text-sm font-medium">OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="Enter 6-digit OTP"
                    className="octa-input mt-2 tracking-widest text-center text-lg"
                    data-testid="partner-otp-input"
                  />
                  {mockOtp && (
                    <p className="text-xs text-[#737373] mt-2">
                      Mock OTP for testing: <strong>{mockOtp}</strong>
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full btn-primary-octa font-semibold disabled:opacity-50"
                data-testid={otpSent ? "partner-verify-otp-btn" : "partner-send-otp-btn"}
              >
                {loading ? "Please wait..." : otpSent ? "Verify OTP" : "Send OTP"}
              </button>

              {otpSent && (
                <button
                  type="button"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                    setMockOtp("");
                  }}
                  className="w-full text-sm text-[#4A4A4A] hover:text-[#1A1A1A]"
                  data-testid="partner-change-mobile-btn"
                >
                  Change mobile number
                </button>
              )}
            </form>

            <div className="mt-10 text-sm text-[#737373]">
              Looking to book a service?{" "}
              <Link to="/customer/login" className="text-[#E06D53] font-semibold">
                Customer Login →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
